import React, { useState, useEffect } from 'react'
import styled from 'styled'

import { SharedActionCreators, sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { Button, ButtonGroup} from 'components/ui/button'
import { FooterEditEntity } from 'components/ui/footer'
import { IconPlus } from 'components/ui/icons'
import { Confirm } from 'components/ui/modal'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { SectionHeader } from 'components/ui/section-header'
import { SelectFilter, SelectFilterItem } from 'components/ui/select-filter'
import { StatusGuard } from 'components/ui/status-guard'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { GettyStore, GettyAppState, VisitorInfoPages } from 'domains/org/getty/store'
import { getSearchFilterValues, saveSearchFilterValue } from 'domains/org/getty/utils/search-filter'
import { ValidationFailuresList } from 'domains/org/getty/components/publishing/validationsList'
import { ErrorsList } from 'domains/org/getty/components/publishing/errorsList'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { AmenityCreate } from './amenity-create'
import { ReorderablePages } from './reorderable-pages'
import { VisitorInfoCreate } from './visitor-info-create'

import { Site } from 'generated/mos/structure'
import { VisitorInfoSiteContent, VisitorInfoPage, Amenity } from 'generated/mos/org/getty/visitorinfo'
import { CreateVisitorInfoPageRequest } from 'generated/mos/org/getty/visitorinfomanagement'

import { areObjectsEqual, pick, pluralise } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { space } from 'helpers/style'

import { getSubnav } from '../subnav'

import { DEFAULT_SITE_REF_ID } from '../../constants'

import { visitorInfoSchema } from '../../schemas/visitorInfo'

const FilterButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: -${space(6)};
  margin-bottom: ${space(3)};
`;

const NoResults = styled.div`
  text-align: center;
  padding-top: ${space(5)};
  padding-bottom: ${space(5)};
`;

type ActionProps =
  Pick<GettyActionCreators, 'gettyListSitesRequest'  | 'gettyUpdateVisitorInfoSiteContentRequest' | 'gettyListVisitorInfoPagesRequest' | 'gettyCreateVisitorInfoPageRequest'> &
  Pick<SharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyAppState, | 'sites' | 'categories' | 'categoryDelete' | 'visitorInfo' | 'visitorInfoCreate' | 'visitorInfoDelete' | 'amenityCreate' | 'amenityDelete'>;

type DirectProps = {
  projectId: string;
  historyPush: (id: string) => void;
  navBackRoute?: string;
};

type Props = ActionProps & ConnectedProps & DirectProps;

const VisitorInfoView = (props: Props) => {

  const {
    projectId,
    gettyListSitesRequest,
    toastNotification,
    sites,
    categories,
    historyPush,
    gettyUpdateVisitorInfoSiteContentRequest,
    gettyListVisitorInfoPagesRequest,
    gettyCreateVisitorInfoPageRequest,
    visitorInfo,
    visitorInfoDelete,
    visitorInfoCreate,
    amenityDelete,
  } = props;

  const visitorInfoData = statusSelector.data(visitorInfo)

  const [ showVisitorInfoCreate, setShowVisitorInfoCreate ] = useState<boolean>(false)
  const [ showWarning, setShowWarning ] = useState<boolean>(false)
  const [ warningConfirm, setWarningConfirm ] = useState<() => void>(() => {})
  const [ createAmenity, setCreateAmenity ] = useState<VisitorInfoPage.Ref | undefined>(undefined)
  const [ orderedPages, setOrderedPages ] = useState<VisitorInfoPages['pages'] | undefined>(visitorInfoData && visitorInfoData.pages)

  const initialOrder = visitorInfoData && visitorInfoData.pages
  const modified = !!initialOrder && !!orderedPages && !areObjectsEqual(initialOrder, orderedPages)

  const filterValues = sessionStorage.length && getSearchFilterValues()
  const sessionSiteId = filterValues.visitorInfoLocationRef ? filterValues.visitorInfoLocationRef.id : DEFAULT_SITE_REF_ID

  const selectedSite =
    sites.status === Status.Ready &&
    sites.data.sites &&
    sites.data.sites.filter(site => Site.mustRef(site.ref).id === sessionSiteId)[0]

  const defaultSiteRef = {
    typename: Site.refName,
    id: DEFAULT_SITE_REF_ID
  }

  useEffect(() => {
    gettyListSitesRequest()
  }, [gettyListSitesRequest])

  useEffect(() => {
    if(selectedSite && selectedSite.ref ||
      visitorInfoDelete.status === Status.Ready ||
      amenityDelete.status === Status.Ready ||
      visitorInfoCreate.status === Status.Ready
      && visitorInfoCreate.data.pageProperties
      && visitorInfoCreate.data.pageProperties.type === VisitorInfoPage.SectionSeparator.refName
      && !modified
    ) {
      selectedSite && selectedSite.ref && gettyListVisitorInfoPagesRequest(selectedSite.ref)
    }
  }, [gettyListVisitorInfoPagesRequest, selectedSite, visitorInfoDelete.status, visitorInfoCreate, amenityDelete.status ]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (visitorInfo.status === Status.Ready) setOrderedPages(visitorInfo.data.pages)
    if (visitorInfo.status === Status.Stale && selectedSite && selectedSite.ref) gettyListVisitorInfoPagesRequest(selectedSite.ref)
  }, [visitorInfo, visitorInfo.status, selectedSite, gettyListVisitorInfoPagesRequest])

  const siteOpts = sites.status === Status.Ready && sites.data.sites
      ? sites.data.sites.map((site: Site.Entity) => ({
        label: site.name,
        value: site,
      }))
      : []

  const saveHandler = () => {
    if(!orderedPages) {
      toastNotification({
        type: 'error',
        text: 'Visitor Info Page could not be updated',
      })
    } else {
      orderedPages && visitorInfoData && gettyUpdateVisitorInfoSiteContentRequest({
        siteContentRef: visitorInfoData.siteContentRef,
        pages: orderedPages,
      })
      toastNotification({
        type: 'success',
        text: 'Visitor Info Page updated',
      })
    }
  }

  const createDivider = (siteContentRef: VisitorInfoSiteContent.Ref) => {
    gettyCreateVisitorInfoPageRequest({
      ...CreateVisitorInfoPageRequest.defaults,
      siteContentRef,
      listTitle: visitorInfoSchema.listTitle.write('Divider'),
      pageProperties: VisitorInfoPage.SectionSeparator.defaults,
    })
    toastNotification({
      type: 'success',
      text: 'Divider successfully created',
    })
  }

  const updateHandler = (page: VisitorInfoPage.Entity) => {
    const slug = page.pageProperties && page.ref ? `/${page.ref.id}` : ''
    const path = `/project/${projectId}/visitor-info${slug}`
    if(modified) {
      setShowWarning(true)
      setWarningConfirm(() => () => historyPush(path))
    } else historyPush(path)
  }

  const updateAmenityHandler = (amenity: Amenity.Entity) => {
    const slug = amenity.ref ? `/amenity/${amenity.ref.id}` : ''
    const path = `/project/${projectId}/visitor-info${slug}`
    if(modified) {
      setShowWarning(true)
      setWarningConfirm(() => () => historyPush(path))
    } else historyPush(path)
  }

  const createAmenityHandler = (ref: VisitorInfoPage.Ref) => {
    if(modified) {
      setShowWarning(true)
      setWarningConfirm(() => () => setCreateAmenity(ref))
    } else setCreateAmenity(ref)
  }

  const deleteHandler = (page: VisitorInfoPage.Entity) => {
    const updatedOrderedPages = orderedPages && orderedPages.filter(item => VisitorInfoPage.mustRef(item.page.ref).id !== VisitorInfoPage.mustRef(page.ref).id)
    setOrderedPages(updatedOrderedPages)
  }

  const deleteAmenityHandler = (page: VisitorInfoPage.Entity, amenity: Amenity.Entity) => {
    const updatedOrderedPages = orderedPages &&
      orderedPages.map(pageItem => {
        if(VisitorInfoPage.mustRef(pageItem.page.ref).id === VisitorInfoPage.mustRef(page.ref).id && pageItem.amenities) {
          const amenities = pageItem.amenities.filter(amenityItem => Amenity.mustRef(amenityItem.ref).id !== Amenity.mustRef(amenity.ref).id)
          return {
            ...pageItem,
            amenities,
          }
        }
        return pageItem
    })
    setOrderedPages(updatedOrderedPages)
  }

  return (
    <LayoutListOrFormPage
      nav={() => (
        <PrimaryNavigationEditEntity
          title="Edit Project"
          navBackRoute={"/"}
          onConfirm={
            modified ? () => {
              historyPush("/")
            }
            : undefined
          }
          darkTheme
        />
      )}
      title="GettyGuide"
      toolbar={() => (
        <ButtonGroup>
          <ValidationFailuresList projectId={projectId} />
          <Button appearance="primary" onClick={() => props.historyPush(`/project/${projectId}/publishing`)}>
            Publish
          </Button>
        </ButtonGroup>
      )}
      subnav={() => (
        <>
          <ErrorsList projectId={projectId} />
          <SecondaryNavigation navItems={getSubnav(projectId)} modified={modified} historyPush={historyPush} >
            <Button variant="subtle" onClick={() =>  {
                if(modified) {
                  setShowWarning(true)
                  setWarningConfirm(() => () => setShowVisitorInfoCreate(true))
                } else setShowVisitorInfoCreate(true)
              }}>
              <IconPlus size="small" />Create Page
            </Button>
            <Button variant="subtle" onClick={() => {
                if(modified) {
                  if(visitorInfoData) {
                    setShowWarning(true)
                    setWarningConfirm(() => () => VisitorInfoSiteContent.mustRef(visitorInfoData.siteContentRef))
                  }
                } else visitorInfoData && createDivider(VisitorInfoSiteContent.mustRef(visitorInfoData.siteContentRef))
              }}>
              <IconPlus size="small" />Create Divider
            </Button>
          </SecondaryNavigation>
        </>
      )}
      content={() => (
        <>
          {showVisitorInfoCreate && visitorInfoData && visitorInfoData.siteContentRef &&
            <VisitorInfoCreate
              projectId={projectId}
              onCancel={() => { setShowVisitorInfoCreate(false) }}
              historyPush={historyPush}
              siteContentRef={VisitorInfoSiteContent.mustRef(visitorInfoData.siteContentRef)}
            />
          }
          {createAmenity && visitorInfoData &&
            <AmenityCreate
              projectId={projectId}
              historyPush={historyPush}
              onCancel={() => setCreateAmenity(undefined)}
              visitorInfoRef={createAmenity}
            />
          }
          {showWarning &&
            <Confirm
              onClose={() => setShowWarning(false)}
              title="Unsaved Changes"
              message="Do you want to discard your changes?"
              confirmLabel="Yes, discard"
              closeLabel="Cancel"
              onConfirm={() => {
                warningConfirm()
                setShowWarning(false)
              }}
            />
          }
          <FilterButtonRow>
            {sites &&
              <SelectFilter value={selectedSite ? selectedSite.name: ''}>
                {siteOpts.map(site => (
                  <SelectFilterItem key={site.label} selected={selectedSite && selectedSite.name === site.label} onClick={() => {
                    const selectedSite = site.value.ref ? site.value.ref : defaultSiteRef
                    if(modified) {
                      setShowWarning(true)
                      setWarningConfirm(() => () => {
                        saveSearchFilterValue( {visitorInfoLocationRef: selectedSite} )
                        gettyListVisitorInfoPagesRequest(selectedSite)
                      })
                    } else {
                      saveSearchFilterValue( {visitorInfoLocationRef: selectedSite} )
                      gettyListVisitorInfoPagesRequest(selectedSite)
                    }
                  }}>
                    {site.label}
                  </SelectFilterItem>
                ))}
              </SelectFilter>
            }
            <ButtonGroup>
            </ButtonGroup>
          </FilterButtonRow>

          <StatusGuard status={visitorInfo}>
            {orderedPages && visitorInfoData && orderedPages.length  ?
              <>
                <SectionHeader heading={`${visitorInfoData.pages.length} ${pluralise(visitorInfoData.pages.length, 'page', 'pages')}`} />
                <ReorderablePages
                  orderedPages={orderedPages}
                  setOrderedPages={setOrderedPages}
                  updateHandler={updateHandler}
                  deleteHandler={deleteHandler}
                  updateAmenityHandler={updateAmenityHandler}
                  deleteAmenityHandler={deleteAmenityHandler}
                  createAmenityHandler={createAmenityHandler}
                />
              </>
              : <NoResults>
                <h3>No Vistor Info</h3>
                <p>This site has no vistor info. Add or create some vistor info.</p>
              </NoResults>
            }
          </StatusGuard>
        </>
      )}
      footer={() => categories && (
        <FooterEditEntity
          entity={categories}
          modified={modified}
          onCancel={() => {
            modified && selectedSite && selectedSite.ref && gettyListVisitorInfoPagesRequest(selectedSite.ref)
          }}
          onSave={saveHandler}
        />
      )}
    />
  )
}

export const VisitorInfo = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => pick(store.getty, 'sites', 'categories','categoryDelete', 'visitorInfo', 'visitorInfoCreate', 'visitorInfoDelete' , 'amenityCreate', 'amenityDelete'),
  {
    ...pick(actionCreators, 'gettyListSitesRequest', 'gettyDeleteTourCategoryRequest', 'gettyUpdateVisitorInfoSiteContentRequest' , 'gettyListVisitorInfoPagesRequest', 'gettyCreateVisitorInfoPageRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(VisitorInfoView);
